import { useState } from 'react';
import { toast } from 'sonner';

export function useGymFinder(api) {
  const [showGymFinder, setShowGymFinder] = useState(false);
  const [searchingGyms, setSearchingGyms] = useState(false);
  const [nearbyGyms, setNearbyGyms] = useState([]);
  const [gymSearchQuery, setGymSearchQuery] = useState('');
  const [expandedGymId, setExpandedGymId] = useState(null);
  const [gymDetails, setGymDetails] = useState({});
  const [loadingGymDetails, setLoadingGymDetails] = useState(null);

  const searchGyms = async () => {
    if (!gymSearchQuery.trim()) return;
    setSearchingGyms(true);
    try {
      const response = await api.post('/gyms/search', {
        query: gymSearchQuery,
        max_results: 20
      });
      setNearbyGyms(response.gyms || []);
      if (response.gyms?.length === 0) {
        toast.info('No gyms found. Try a different search term.');
      }
    } catch (err) {
      console.error('Error searching gyms:', err);
      toast.error('Failed to search gyms. Please try again.');
    } finally {
      setSearchingGyms(false);
    }
  };

  const findNearbyGyms = async () => {
    setSearchingGyms(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setSearchingGyms(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await api.post('/gyms/nearby', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            radius_meters: 5000,
            max_results: 20
          });
          setNearbyGyms(response.gyms || []);
          if (response.gyms?.length === 0) {
            toast.info('No gyms found nearby. Try searching by name.');
          } else {
            toast.success(`Found ${response.gyms.length} gyms near you!`);
          }
        } catch (err) {
          console.error('Error finding nearby gyms:', err);
          toast.error('Failed to find nearby gyms. Please try again.');
        } finally {
          setSearchingGyms(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Could not get your location. Please search by name instead.');
        setSearchingGyms(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const fetchGymDetails = async (placeId) => {
    if (gymDetails[placeId]) {
      setExpandedGymId(expandedGymId === placeId ? null : placeId);
      return;
    }
    setLoadingGymDetails(placeId);
    setExpandedGymId(placeId);
    try {
      const response = await api.get(`/gyms/details/${placeId}`);
      setGymDetails(prev => ({ ...prev, [placeId]: response }));
    } catch (err) {
      console.error('Error fetching gym details:', err);
      toast.error('Could not load gym details');
      setGymDetails(prev => ({ ...prev, [placeId]: { error: true } }));
    } finally {
      setLoadingGymDetails(null);
    }
  };

  const getDirections = (gym) => {
    const destination = encodeURIComponent(gym.address || gym.name);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${gym.place_id}`, '_blank');
  };

  return {
    showGymFinder,
    setShowGymFinder,
    searchingGyms,
    nearbyGyms,
    gymSearchQuery,
    setGymSearchQuery,
    expandedGymId,
    gymDetails,
    loadingGymDetails,
    searchGyms,
    findNearbyGyms,
    fetchGymDetails,
    getDirections,
  };
}
